import { useUnifiedStore } from '../stores/unifiedStore';
import { DeviceManager } from './DeviceManager';
import { audioManager } from './audio';
import { AudioFrameConstants } from '../constants/AudioFrameConstants';
import { mqttPttService } from './mqttPttService';
import { authService } from './authService';
import { PttStateValidator } from '../utils/stateValidator';


/**
 * 状态同步服务
 * 负责将各个服务的状态同步到统一的状态管理中
 */
export class StateSyncService {
  private static instance: StateSyncService;
  private unsubscribeFunctions: (() => void)[] = [];
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): StateSyncService {
    if (!StateSyncService.instance) {
      StateSyncService.instance = new StateSyncService();
    }
    return StateSyncService.instance;
  }

  /**
   * 初始化状态同步（基础初始化，不包含需要认证的服务）
   */
  public async initialize(): Promise<void> {
    console.log('StateSyncService: 初始化基础状态同步服务');
    
    // 只初始化不需要认证的服务
    this.syncAuthServiceState();
    
    this.isInitialized = true;
  }

  /**
   * 初始化需要认证的服务（在用户登录后调用）
   */
  public async initializeAuthenticatedServices(): Promise<void> {
    if (!this.isInitialized) {
      console.warn('StateSyncService: 基础服务未初始化，先初始化基础服务');
      await this.initialize();
    }
    
    console.log('StateSyncService: 初始化需要认证的服务');
    
    // 检查认证状态
    if (!authService.isAuthenticated()) {
      console.warn('StateSyncService: 用户未认证，跳过需要认证的服务初始化');
      return;
    }
    
    try {
      // 设置超时机制，防止初始化时间过长
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('服务初始化超时')), 15000); // 15秒超时
      });
      
      const initPromise = this.performAuthenticatedServicesInitialization();
      
      // 使用Promise.race来设置超时
      await Promise.race([initPromise, timeoutPromise]);
      
      console.log('StateSyncService: 需要认证的服务初始化完成');
    } catch (error) {
      console.error('StateSyncService: 需要认证的服务初始化失败:', error);
      // 不抛出错误，允许应用继续运行
      console.warn('StateSyncService: 服务初始化失败，但应用将继续运行');
    }
  }

  /**
   * 执行需要认证的服务初始化
   */
  private async performAuthenticatedServicesInitialization(): Promise<void> {
    try {
      // 初始化设备管理器
      const deviceManager = DeviceManager.getInstance();
      console.log('StateSyncService: 开始初始化设备管理器');
      await deviceManager.initialize();
      console.log('StateSyncService: 设备管理器初始化完成');
      
      // 同步设备管理器状态
      console.log('StateSyncService: 开始同步设备管理器状态');
      this.syncDeviceManagerState();
      console.log('StateSyncService: 设备管理器状态同步完成');
      
      // 同步音频管理器状态
      console.log('StateSyncService: 开始同步音频管理器状态');
      this.syncAudioManagerState();
      console.log('StateSyncService: 音频管理器状态同步完成');
      
      // 同步PTT服务状态
      console.log('StateSyncService: 开始同步PTT服务状态');
      this.syncPttServiceState();
      console.log('StateSyncService: PTT服务状态同步完成');
      
    } catch (error) {
      console.error('StateSyncService: 执行认证服务初始化失败:', error);
      throw error;
    }
  }

  /**
   * 同步设备管理器状态
   */
  private syncDeviceManagerState(): void {
    const deviceManager = DeviceManager.getInstance();
    const { actions } = useUnifiedStore.getState();

    // 监听设备状态变化
    const handleDeviceStatusChange = () => {
      const devices = deviceManager.getAllDevices();
      console.log(`🔄 [状态同步] 设备状态变化 - 设备数量: ${devices.length}`, {
        devices: devices.map(d => ({ id: d.getId(), name: d.getInfo().name, status: d.getStatus() })),
        timestamp: new Date().toISOString()
      });
      actions.setDevices(devices);
    };

    // 监听设备列表更新
    const handleDeviceListUpdate = (devices: any[]) => {
      console.log(`🔄 [状态同步] 设备列表更新 - 设备数量: ${devices.length}`, {
        devices: devices,
        timestamp: new Date().toISOString()
      });
      actions.setDevices(devices);
    };

    // 监听统计信息更新
    const handleStatisticsUpdate = (statistics: any) => {
      const deviceStats = {
        totalDevices: statistics.total,
        onlineDevices: statistics.online,
        devicesWithUdpPort: deviceManager.getAllDevices().length
      };
      console.log(`🔄 [状态同步] 设备统计信息更新:`, {
        statistics: deviceStats,
        originalStats: statistics,
        timestamp: new Date().toISOString()
      });
      actions.setDeviceStatistics(deviceStats);
    };

    // 监听连接状态变化
    const handleConnectionStateChange = (state: string) => {
      const isConnected = state === 'connected';
      console.log(`🔄 [状态同步] 设备连接状态变化:`, {
        state: state,
        isConnected: isConnected,
        timestamp: new Date().toISOString()
      });
      actions.setDeviceConnectionStatus(isConnected);
    };

    // 订阅事件
    deviceManager.on('deviceStatusChanged', handleDeviceStatusChange);
    deviceManager.on('deviceListUpdated', handleDeviceListUpdate);
    deviceManager.on('statisticsUpdated', handleStatisticsUpdate);
    deviceManager.on('connectionStateChanged', handleConnectionStateChange);

    // 保存取消订阅函数
    this.unsubscribeFunctions.push(() => {
      deviceManager.off('deviceStatusChanged', handleDeviceStatusChange);
      deviceManager.off('deviceListUpdated', handleDeviceListUpdate);
      deviceManager.off('statisticsUpdated', handleStatisticsUpdate);
      deviceManager.off('connectionStateChanged', handleConnectionStateChange);
    });
  }

  /**
   * 同步音频管理器状态
   */
  private syncAudioManagerState(): void {
    const { actions } = useUnifiedStore.getState();

    // 监听音频系统状态变化
    const handleActiveDeviceChanged = () => {
      const systemState = audioManager.getState();
      console.log(`🔄 [状态同步] 音频系统状态变化:`, {
        systemState: systemState,
        isInitialized: systemState.isInitialized,
        activeDeviceId: systemState.activeDeviceId,
        timestamp: new Date().toISOString()
      });
      actions.updateAudioSystemState(systemState);
      actions.setAudioInitialized(systemState.isInitialized);
    };

    // 监听音频数据
    const handleAudioData = (audioData: ArrayBuffer) => {
      // 这里可以计算音频电平和频谱
      // 暂时使用简单的音量计算
      const audioLevel = this.calculateAudioLevel(audioData);
      console.log(`🔄 [状态同步] 音频数据更新:`, {
        audioDataSize: audioData.byteLength,
        audioLevel: audioLevel,
        timestamp: new Date().toISOString()
      });
      actions.updateAudioLevel(audioLevel);
    };

    // 订阅事件
    audioManager.on('activeDeviceChanged', handleActiveDeviceChanged);
    audioManager.on('audioData', handleAudioData);

    // 保存取消订阅函数
    this.unsubscribeFunctions.push(() => {
      audioManager.off('activeDeviceChanged', handleActiveDeviceChanged);
      audioManager.off('audioData', handleAudioData);
    });
  }

  /**
   * 同步PTT服务状态
   */
  private syncPttServiceState(): void {
    const { actions } = useUnifiedStore.getState();

    // 监听设备选择变化，当设备变化时获取PTT状态
    const handleDeviceSelectionChange = async () => {
      try {
        const state = useUnifiedStore.getState();
        const selectedDevice = state.devices.selectedDevice;
        
        if (!selectedDevice) {
          console.log(`🔄 [状态同步] PTT服务状态同步：没有选中的设备，清除PTT状态`, {
            timestamp: new Date().toISOString()
          });
          // 使用安全的状态更新
          const { actions } = useUnifiedStore.getState();
          actions.setPttActive(false);
          actions.setPttError(null);
          return;
        }

        console.log(`🔄 [状态同步] PTT服务状态同步：设备选择变化`, {
          selectedDeviceId: selectedDevice.getInfo().id,
          selectedDeviceName: selectedDevice.getInfo().name,
          timestamp: new Date().toISOString()
        });

        // 获取新选中设备的PTT状态
        const response = mqttPttService.getPttStatus(selectedDevice.getInfo().id);
        if (response) {
          const isActive = response.isActive || false;
          
          console.log(`🔄 [状态同步] PTT状态更新:`, {
            deviceId: selectedDevice.getInfo().id,
            isActive: isActive,
            response: response,
            timestamp: new Date().toISOString()
          });
          
          // 使用安全的状态更新
          const { actions } = useUnifiedStore.getState();
          actions.setPttActive(isActive);
          actions.setPttError(null);
        }
      } catch (error) {
        console.error('获取PTT状态失败:', error);
        const errorMessage = error instanceof Error ? error.message : '获取PTT状态失败';
        actions.setPttError(errorMessage);
      }
    };

    // 监听设备选择变化
    const unsubscribe = useUnifiedStore.subscribe(
      (state) => state.devices.selectedDevice,
      (selectedDevice) => {
        if (selectedDevice) {
          handleDeviceSelectionChange();
        }
      }
    );

    // 初始获取状态
    handleDeviceSelectionChange();

    // 定期验证状态一致性
    const validationInterval = setInterval(() => {
      const currentState = useUnifiedStore.getState();
      const validation = PttStateValidator.validatePttState(currentState);
      
      if (!validation.isValid) {
        console.warn('PTT状态一致性检查失败:', validation.errors);
        // 可以在这里添加自动修复逻辑
      }
    }, 10000); // 每10秒检查一次

    // 保存清理函数
    this.unsubscribeFunctions.push(unsubscribe);
    this.unsubscribeFunctions.push(() => clearInterval(validationInterval));

    console.log('PTT服务状态同步：已启动事件驱动机制和状态验证，简化设备状态服务已集成');
  }

  /**
   * 同步认证服务状态
   */
  private syncAuthServiceState(): void {
    const { actions } = useUnifiedStore.getState();

    try {
      // 初始化时同步当前认证状态
      const user = authService.getUser();
      const isAuthenticated = authService.isAuthenticated();
      
      if (user && isAuthenticated) {
        actions.setUser(user);
      }
    } catch (error) {
      console.error('同步认证服务状态失败:', error);
      // 不抛出错误，避免阻止其他服务的初始化
    }
  }

  /**
   * 计算音频电平
   */
  private calculateAudioLevel(audioData: ArrayBuffer): number {
    const samples = new Int16Array(audioData);
    let sum = 0;
    
    for (let i = 0; i < samples.length; i++) {
      sum += Math.abs(samples[i]);
    }
    
    const average = sum / samples.length;
    return Math.min(average / (AudioFrameConstants.BIT_DEPTH === 16 ? 32768 : 2147483648), 1); // 归一化到0-1
  }

  /**
   * 清理资源
   */
  public dispose(): void {
    console.log('StateSyncService: 清理状态同步服务');
    
    // 取消所有订阅
    this.unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    this.unsubscribeFunctions = [];
    
    // 重置初始化状态
    this.isInitialized = false;
    
    // 开发环境下，重置单例实例
    if (process.env.NODE_ENV === 'development') {
      console.log('开发环境：重置StateSyncService单例实例');
      StateSyncService.instance = null as any;
    }
  }
}
